import { Icon } from '@capsico/ui';
import { Button } from './ui';

/*
 * W23 "please update" (presentation only): the update sheet. A required update is a blocking
 * full-height sheet (no close, no scrim tap); an optional one can be put off with "Later".
 * State and data live in hooks/useAppUpdate.ts.
 */

export interface UpdateSheetLabels {
  title: string;
  body: string;
  /** "Version 1.4.2", or null when the server sent no version name. */
  version: string | null;
  notesTitle: string;
  action: string;
  later: string;
}

export function UpdateSheet({
  required,
  reload,
  notes,
  labels,
  onUpdate,
  onLater,
}: {
  required: boolean;
  /** Web builds reload instead of opening a store. */
  reload: boolean;
  notes: string | null;
  labels: UpdateSheetLabels;
  onUpdate: () => void;
  onLater: () => void;
}) {
  return (
    <div
      className={`fixed inset-0 z-[80] flex flex-col justify-end ${required ? 'pt-[env(safe-area-inset-top)]' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-update-title"
    >
      <div
        className="absolute inset-0 bg-scrim"
        aria-hidden="true"
        onClick={required ? undefined : onLater}
      />
      <div
        className={`relative mx-auto flex w-full max-w-120 flex-col rounded-t-sheet bg-surface pb-[env(safe-area-inset-bottom)] animate-[rise_200ms_ease-out] ${required ? 'h-full' : 'max-h-sheet-max'}`}
      >
        <div className={`min-h-0 overflow-y-auto px-5 pt-6 ${required ? 'flex-1' : ''}`}>
          <span className="inline-flex size-12 items-center justify-center rounded-full bg-brand-soft text-brand-ink">
            <Icon name="download" size={24} />
          </span>
          <h2 id="app-update-title" className="mt-3 text-lg font-bold text-ink">
            {labels.title}
          </h2>
          <p className="mt-1 text-sm text-ink-2">{labels.body}</p>
          {labels.version && (
            <span className="mt-3 inline-flex rounded-full bg-tile px-2 py-0.5 text-caption text-ink-2 tabular">
              {labels.version}
            </span>
          )}
          {notes && (
            <div className="mt-4 rounded-field bg-brand-soft px-3 py-2">
              <p className="text-micro font-semibold tracking-wide text-ink-2 uppercase">
                {labels.notesTitle}
              </p>
              <p className="mt-0.5 text-xs whitespace-pre-line text-ink">{notes}</p>
            </div>
          )}
        </div>
        <div className="mt-4 space-y-3 px-5 py-4 shadow-[inset_0_1px_0_var(--color-hair)]">
          <Button full onClick={onUpdate}>
            <Icon name={reload ? 'refresh' : 'download'} size={18} />
            {labels.action}
          </Button>
          {!required && (
            <button
              type="button"
              onClick={onLater}
              className="block w-full text-center text-sm font-semibold text-ink-2"
            >
              {labels.later}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
